import { useEffect, useState } from 'react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';

import { SelectOptionsT } from '../../../../../components/SelectDropdown/SelectDropdown';
import { RoutesPathE } from '../../../../../declarations/enums/routes';
import useReactRouter from '../../../../../hooks/useReactRouter';

const INITIAL_SEGMENT = {
  destination: {
    latitude: 0,
    longitude: 0,
    name: '',
  },
  numberOfPassengers: 1,
  numberOfVehicles: 1,
  order: 0,
  origin: {
    latitude: 0,
    longitude: 0,
    name: '',
  },
  roundTrip: false,
  transportType: '',
  vehicleType: '',
};

export type QuerySegmentT = typeof INITIAL_SEGMENT;

const useTransportationForm = () => {
  const { navigateTo, getUrlQueryParams } = useReactRouter();
  const {
    suggestions: { data },
    setValue,
  } = usePlacesAutocomplete({
    debounce: 300,
  });
  const queryObj = getUrlQueryParams<{ segments?: QuerySegmentT[] }>();
  const [locations, setLocations] = useState<SelectOptionsT[]>([]);
  const [segments, setSegments] = useState(queryObj?.segments || [INITIAL_SEGMENT]);
  const [isAddClicked, setAddSClicked] = useState(false);
  const [hasErrors, setErrors] = useState(false);

  const handleOnChangeLocation = async (name: string, fieldName: string, segmentIndex: number) => {
    let lat = 0;
    let lng = 0;

    if (name) {
      const geocode = await getGeocode({ address: name });
      const latLng = await getLatLng(geocode[0]);
      lat = latLng.lat;
      lng = latLng.lng;
    }

    handleChangeSegment(
      {
        [fieldName]: {
          latitude: lat,
          longitude: lng,
          name,
        },
      },
      segmentIndex
    );

    // copy destination value for next segment origin when add new segment
    if (segmentIndex !== segments.length - 1 && fieldName === 'destination' && name) {
      handleChangeSegment(
        {
          origin: {
            latitude: lat,
            longitude: lng,
            name,
          },
        },
        segmentIndex + 1
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangeSegment = (val: any, index: number) => {
    const updatedSegments = segments.map((segment, arrayIndex) =>
      arrayIndex === index ? { ...segment, ...val } : segment
    );

    setSegments(updatedSegments);
  };

  const handleAddNewSegment = (isRequiredValuesMissed: boolean) => {
    setAddSClicked(true);

    if (isRequiredValuesMissed) {
      return;
    }

    setSegments([
      ...segments,
      { ...INITIAL_SEGMENT, origin: segments[segments.length - 1].destination },
    ]);
  };

  const handleOnDeleteSegment = (index: number) => {
    setSegments(segments.filter((_, segmentIndex) => segmentIndex !== index));
  };

  const onClickOffset = () => {
    navigateTo(RoutesPathE.CARBON_OFFSETS_PAGE, { segments }, true);
  };

  useEffect(() => {
    const hasError = segments.some(
      ({ origin, destination, vehicleType }) => !vehicleType || !origin.name || !destination.name
    );

    setErrors(hasError);
    setAddSClicked(false);
  }, [segments]);

  useEffect(() => {
    setLocations(
      data.map(({ description }: { description: string }) => ({
        value: description,
        label: description,
      }))
    );
  }, [data]);

  return {
    isAddClicked,
    hasErrors,
    locations,
    segments,
    setValue,
    onClickOffset,
    handleOnChangeLocation,
    handleAddNewSegment,
    handleOnDeleteSegment,
    handleChangeSegment,
  };
};

export default useTransportationForm;
